"use client";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { copyTableToClipboard, exportAsCSV } from "@/lib/export";
import { sendGTMEvent } from "@next/third-parties/google";
import { cn } from "@/lib/utils";
import { CheckIcon, ClipboardIcon, DownloadIcon, LinkIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { useStore } from "zustand";
import { useGrade, useGradingTable } from "./useGrade";
import { useURLSync } from "./useURLSync";

export default function GradingTables() {
  const {
    possibleScores,
    passingGrade,
    maxScore,
    term,
    linearPercentage,
    pointsType,
  } = useGradingTable();
  // term and linearPercentage used for CSV export
  const { highlightedScore } = useStore(useGrade, (store) => store);
  const { copyShareLink } = useURLSync();
  const [tableCopied, setTableCopied] = useState(false);
  const [shareLinkCopied, setShareLinkCopied] = useState(false);

  const handleExportCSV = () => {
    exportAsCSV(possibleScores, {
      passingGrade,
      maxScore,
      term,
      linearPercentage,
      pointsType,
    });
    sendGTMEvent({ event: "exportCSV" });
  };

  const handleCopyTable = async () => {
    const success = await copyTableToClipboard(possibleScores, maxScore);
    if (success) {
      setTableCopied(true);
      sendGTMEvent({ event: "copyTable" });
      setTimeout(() => setTableCopied(false), 2000);
    }
  };

  const handleCopyLink = async () => {
    const success = await copyShareLink();
    if (success) {
      setShareLinkCopied(true);
      sendGTMEvent({ event: "shareLinkCopied" });
      setTimeout(() => setShareLinkCopied(false), 2000);
    }
  };

  const passedAndFailedColumns = useMemo(() => {
    const passed = possibleScores
      .filter((x) => x.grade >= Number(passingGrade))
      .reverse();
    const failed = possibleScores
      .filter((x) => x.grade < Number(passingGrade))
      .reverse();
    return {
      passed,
      failed,
    };
  }, [possibleScores, passingGrade]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col xl:flex-row xl:items-start md:justify-between gap-3">
        <div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            Cijfertabel
          </h2>
          <p className="text-muted-foreground text-base md:text-lg mt-2 max-w-lg">
            Overzicht van alle scores en cijfers voor jouw toets
          </p>
        </div>
        <div className="flex gap-2 sm:gap-3">
          {/* Share URL */}
          <Button
            type="button"
            variant="outline"
            size="default"
            onClick={handleCopyLink}
            className="gap-2"
            aria-label={shareLinkCopied ? "Link gekopieerd" : "Deel link"}
          >
            {shareLinkCopied ? (
              <>
                <CheckIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Gekopieerd</span>
              </>
            ) : (
              <>
                <LinkIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Delen</span>
              </>
            )}
          </Button>

          <Button
            variant="outline"
            size="default"
            onClick={handleCopyTable}
            className="gap-2"
            aria-label={tableCopied ? "Tabel gekopieerd" : "Kopieer tabel"}
          >
            {tableCopied ? (
              <>
                <CheckIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Gekopieerd</span>
              </>
            ) : (
              <>
                <ClipboardIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Kopieer</span>
              </>
            )}
          </Button>
          <Button variant="secondary" size="default" onClick={handleExportCSV} className="gap-2" aria-label="Download CSV">
            <DownloadIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Download CSV</span>
            <span className="sm:hidden">CSV</span>
          </Button>
        </div>
      </div>

      {/* Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        {/* Passed table */}
        <Card className="overflow-hidden pt-0 pb-0! gap-0 shadow-sm">
          <CardHeader className="bg-success-muted/30 border-b px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="w-2.5 h-2.5 rounded-full bg-success"></span>
                <div>
                  <CardTitle className="text-lg font-semibold text-foreground">
                    Voldoende
                  </CardTitle>
                  <CardDescription className="text-sm mt-0.5">
                    {passedAndFailedColumns.passed.length} scores
                  </CardDescription>
                </div>
              </div>
              <Badge variant="success-outline" className="text-sm px-3 py-1">
                {passingGrade}+
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-auto max-h-180">
              <Table>
                <TableHeader className="sticky top-0 bg-background/95 backdrop-blur-sm border-b">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-24 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wider py-4">
                      Score
                    </TableHead>
                    <TableHead className="w-24 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wider py-4">
                      Fouten
                    </TableHead>
                    <TableHead className="text-center text-xs font-semibold text-muted-foreground uppercase tracking-wider py-4">
                      Cijfer
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {passedAndFailedColumns.passed.map((score) => {
                    const juist = score.score;
                    const onjuist = maxScore - score.score;
                    const isHighlighted = highlightedScore === score.score;
                    return (
                      <TableRow
                        key={score.score}
                        data-score={score.score}
                        className={cn(
                          "hover:bg-muted/40 transition-colors duration-500",
                          isHighlighted && "bg-primary/20"
                        )}
                      >
                        <TableCell className="text-center font-semibold text-base py-3.5">
                          {juist}
                        </TableCell>
                        <TableCell className="text-center text-muted-foreground text-base py-3.5">
                          {onjuist}
                        </TableCell>
                        <TableCell className="text-center py-3.5">
                          <span className="font-bold text-success text-base">
                            {score.grade.toFixed(1)}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Failed table */}
        <Card className="overflow-hidden pt-0 pb-0! gap-0 shadow-sm">
          <CardHeader className="bg-warning-muted/30 border-b px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="w-2.5 h-2.5 rounded-full bg-warning"></span>
                <div>
                  <CardTitle className="text-lg font-semibold text-foreground">
                    Onvoldoende
                  </CardTitle>
                  <CardDescription className="text-sm mt-0.5">
                    {passedAndFailedColumns.failed.length} scores
                  </CardDescription>
                </div>
              </div>
              <Badge variant="warning-outline" className="text-sm px-3 py-1">
                &lt;{passingGrade}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-auto max-h-180">
              <Table>
                <TableHeader className="sticky top-0 bg-background/95 backdrop-blur-sm border-b">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-24 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wider py-4">
                      Score
                    </TableHead>
                    <TableHead className="w-24 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wider py-4">
                      Fouten
                    </TableHead>
                    <TableHead className="text-center text-xs font-semibold text-muted-foreground uppercase tracking-wider py-4">
                      Cijfer
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {passedAndFailedColumns.failed.map((score) => {
                    const juist = score.score;
                    const onjuist = maxScore - score.score;
                    const isHighlighted = highlightedScore === score.score;
                    return (
                      <TableRow
                        key={score.score}
                        data-score={score.score}
                        className={cn(
                          "hover:bg-muted/40 transition-colors duration-500",
                          isHighlighted && "bg-primary/20"
                        )}
                      >
                        <TableCell className="text-center font-semibold text-base py-3.5">
                          {juist}
                        </TableCell>
                        <TableCell className="text-center text-muted-foreground text-base py-3.5">
                          {onjuist}
                        </TableCell>
                        <TableCell className="text-center py-3.5">
                          <span className="font-bold text-warning text-base">
                            {score.grade.toFixed(1)}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
